/**
 * FileAttachmentList component - displays uploaded files above the prompt field.
 * Similar to ChatGPT's file attachment UI.
 */

import {
  FileIcon,
  FileJsonIcon,
  FileSpreadsheet,
  FileTextIcon,
  Loader2Icon,
  XCircleIcon,
  XIcon
} from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from '@/components/Dialog';
import { Badge } from '@/components/ui/badge';
import { Spinner } from '@/components/ui/spinner';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/lib/auth';
import { cn } from '@/lib/utils';

export interface FileAttachment {
  error?: string;
  file: File;
  fileId?: string;
  id: string;
  progress?: number;
  status: 'blocked' | 'failed' | 'processing' | 'ready' | 'uploading';
}

interface FileAttachmentItemProps {
  fileAttachment: FileAttachment;
  fullWidth?: boolean;
  onRemove: () => void;
}

interface FileAttachmentListProps {
  className?: string;
  files: FileAttachment[];
  onRemove: (id: string) => void;
}

interface FileContentResponse {
  chunk_count: number;
  content: string;
  total_chars: number;
}

export function FileAttachmentList({
  className,
  files,
  onRemove
}: FileAttachmentListProps) {
  const [isAllFilesModalOpen, setIsAllFilesModalOpen] = useState(false);

  if (files.length === 0) {
    return null;
  }

  const MAX_VISIBLE_FILES = 2;
  const visibleFiles = files.slice(0, MAX_VISIBLE_FILES);
  const remainingCount = files.length - MAX_VISIBLE_FILES;

  return (
    <>
      <div
        className={cn('flex gap-x-2 overflow-x-hidden px-4 pt-4', className)}
      >
        {visibleFiles.map(fileAttachment => (
          <FileAttachmentItem
            fileAttachment={fileAttachment}
            key={fileAttachment.id}
            onRemove={() => {
              onRemove(fileAttachment.id);
            }}
          />
        ))}
        {remainingCount > 0 && (
          <button
            className="bg-alpha-70 hover:bg-alpha-80 flex shrink-0 grow-0 gap-x-3 rounded-md p-2 transition-colors"
            onClick={() => {
              setIsAllFilesModalOpen(true);
            }}
            type="button"
          >
            <div className="bg-background/50 flex size-10 shrink-0 items-center justify-center rounded-sm">
              <FileIcon className="text-muted-foreground size-6" />
            </div>
            <div className="flex min-w-0 items-center text-sm">
              <span className="text-muted-foreground font-medium whitespace-nowrap">
                +{remainingCount} more
              </span>
            </div>
          </button>
        )}
      </div>

      <Dialog
        onOpenChange={setIsAllFilesModalOpen}
        open={isAllFilesModalOpen}
      >
        <DialogContent
          aria-describedby={undefined}
          className="flex max-h-[80vh] max-w-3xl flex-col"
        >
          <DialogHeader>
            <DialogTitle>All Uploaded Files</DialogTitle>
            <DialogDescription>
              {files.length} file{files.length !== 1 ? 's' : ''} attached to
              this session
            </DialogDescription>
          </DialogHeader>
          <DialogBody>
            <div className="mt-4 flex-1 overflow-y-auto">
              <div className="flex flex-col gap-2">
                {files.map(fileAttachment => (
                  <FileAttachmentItem
                    fileAttachment={fileAttachment}
                    fullWidth={true}
                    key={fileAttachment.id}
                    onRemove={() => {
                      onRemove(fileAttachment.id);
                      // Close modal if no files left
                      if (files.length === 1) {
                        setIsAllFilesModalOpen(false);
                      }
                    }}
                  />
                ))}
              </div>
            </div>
          </DialogBody>
        </DialogContent>
      </Dialog>
    </>
  );
}

function FileAttachmentItem({
  fileAttachment,
  fullWidth = false,
  onRemove
}: FileAttachmentItemProps) {
  const { getJWT } = useAuth();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [fileContent, setFileContent] = useState<FileContentResponse | null>(
    null
  );
  const [isLoadingContent, setIsLoadingContent] = useState(false);

  const handleFileClick = async () => {
    // Only allow viewing if file is ready and has a fileId
    if (fileAttachment.status !== 'ready' || !fileAttachment.fileId) {
      return;
    }

    setIsDialogOpen(true);

    // Load content if not already loaded
    if (!fileContent) {
      setIsLoadingContent(true);
      try {
        const auth = await getJWT();
        if (!auth) {
          throw new Error('Authentication required');
        }

        const response = await fetch(
          `${import.meta.env.VITE_API_BASE_URL}/files/${fileAttachment.fileId}/content`,
          {
            headers: {
              Authorization: `Bearer ${auth.accessToken}`
            },
            method: 'GET'
          }
        );

        if (!response.ok) {
          const error = await response
            .json()
            .catch(() => ({ detail: response.statusText }));
          throw new Error(
            error.detail || `Failed to get file content: ${response.status}`
          );
        }

        const content = await response.json();
        setFileContent(content);
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : 'Failed to load file content';
        toast.error('Failed to load file content', {
          description: errorMessage
        });
        setIsDialogOpen(false);
      } finally {
        setIsLoadingContent(false);
      }
    }
  };

  const getFileTypeIcon = () => {
    const filename = fileAttachment.file.name.toLowerCase();
    const extension = filename.split('.').pop() || '';

    switch (extension) {
      case 'csv':
        return <FileSpreadsheet className="size-6 text-green-600" />;
      case 'doc':
      case 'docx':
        return <FileTextIcon className="size-6 text-blue-700" />;
      case 'json':
        return <FileJsonIcon className="size-6 text-yellow-600" />;
      case 'md':
      case 'txt':
        return <FileTextIcon className="size-6 text-blue-600" />;
      case 'pdf':
        return <FileTextIcon className="size-6 text-red-600" />;
      default:
        return <FileIcon className="text-muted-foreground size-6" />;
    }
  };

  const getStatusIcon = () => {
    switch (fileAttachment.status) {
      case 'blocked':
      case 'failed':
        return <XCircleIcon className="size-3.5 text-red-500" />;
      case 'processing':
      case 'uploading':
        return (
          <Loader2Icon className="text-muted-foreground size-3.5 animate-spin" />
        );
      case 'ready':
        return null; // Don't show checkmark when ready
      default:
        return null;
    }
  };

  const getStatusBadge = () => {
    switch (fileAttachment.status) {
      case 'blocked':
        return (
          <Badge
            className="text-xs"
            variant="destructive"
          >
            Blocked
          </Badge>
        );
      case 'failed':
        return (
          <Badge
            className="text-xs"
            variant="destructive"
          >
            Failed
          </Badge>
        );
      case 'processing':
        return (
          <Badge
            className="text-xs"
            variant="secondary"
          >
            Processing...
          </Badge>
        );
      case 'ready':
        return null; // Don't show badge when ready
      case 'uploading':
        return (
          <Badge
            className="text-xs"
            variant="secondary"
          >
            {fileAttachment.progress !== undefined
              ? `${fileAttachment.progress}%`
              : 'Uploading...'}
          </Badge>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <div
        className={cn(
          'group bg-alpha-70 flex gap-x-3 rounded-md p-2 transition-colors',
          fileAttachment.status === 'ready'
            ? 'hover:bg-alpha-80 cursor-pointer'
            : fileAttachment.status === 'failed' ||
                fileAttachment.status === 'blocked'
              ? 'opacity-70'
              : '',
          fileAttachment.status === 'ready' &&
            fileAttachment.fileId &&
            'cursor-pointer',
          fullWidth ? 'w-full' : 'w-64 shrink-0 grow-0'
        )}
        onClick={handleFileClick}
      >
        <div className="bg-background/50 flex size-10 shrink-0 items-center justify-center rounded-sm">
          {getFileTypeIcon()}
        </div>

        <div className="min-w-0 grow text-sm">
          <div className="truncate font-medium">{fileAttachment.file.name}</div>
          <div className="text-muted-foreground flex items-center gap-1.5">
            {getStatusBadge() || (
              <span className="text-xs">
                {fileAttachment.file.type || 'File'}
              </span>
            )}
          </div>
        </div>

        {getStatusIcon() ? (
          <div className="flex-shrink-0">{getStatusIcon()}</div>
        ) : (
          <button
            aria-label="Remove file"
            className="focus:ring-ring ring-offset-background self-center rounded-xs opacity-70 transition-all hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-hidden"
            onClick={e => {
              e.stopPropagation();
              onRemove();
            }}
            type="button"
          >
            <XIcon className="size-4" />
          </button>
        )}
      </div>

      <Dialog
        onOpenChange={setIsDialogOpen}
        open={isDialogOpen}
      >
        <DialogContent
          aria-describedby={undefined}
          className="flex max-h-[80vh] max-w-3xl flex-col"
        >
          <DialogHeader>
            <DialogTitle className="truncate pr-8">
              {fileAttachment.file.name}
            </DialogTitle>
            <DialogDescription>
              {fileContent
                ? `${fileContent.chunk_count} chunk${fileContent.chunk_count !== 1 ? 's' : ''} • ${fileContent.total_chars.toLocaleString()} characters`
                : 'File content'}
            </DialogDescription>
          </DialogHeader>

          <DialogBody>
            {isLoadingContent ? (
              <div className="flex items-center justify-center py-8">
                <Spinner className="size-6" />
              </div>
            ) : fileContent ? (
              <div className="mt-4 flex flex-1 flex-col overflow-hidden">
                <Textarea
                  className="max-h-[60vh] min-h-[300px] flex-1 resize-none font-mono text-sm"
                  readOnly
                  value={
                    fileContent.content.length > 100000
                      ? `${fileContent.content.slice(0, 100000)}\n\n... (Content truncated. Showing first 100,000 characters of ${fileContent.total_chars.toLocaleString()} total)`
                      : fileContent.content
                  }
                />
              </div>
            ) : null}
          </DialogBody>
        </DialogContent>
      </Dialog>
    </>
  );
}
